type BrowserLocation = Pick<Location, "protocol" | "origin">;

type SignOutNavigateOptions = {
  returnTo?: string;
  navigate: false;
};

type SignOutReturnToOptions = {
  returnTo?: string;
  navigate?: true;
};

const DEFAULT_DESKTOP_REMOTE_TIMEOUT_MS = 1_500;
const DEFAULT_DESKTOP_RELOAD_TIMEOUT_MS = 1_800;
const WORKOS_ORGANIZATION_SESSION_KEY = "workos_organization_id";
const WORKOS_LOCAL_STORAGE_KEYS = [
  "workos:refresh-token",
  "workos:user",
  "workos:access-token",
  "workos:expires-at",
  "workos:authentication-method",
  // Zero session token — must be cleared so a different user signing in
  // doesn't rehydrate the previous user's 12h JWT.
  "ano-zero-sync-token",
  // NOTE: "ano-pending-invite-token" intentionally NOT cleared here —
  // it must survive logout so invite links work across sign-out/sign-in.
];

export type AuthKitSignOut = {
  (options?: SignOutReturnToOptions): void;
  (options?: SignOutNavigateOptions): Promise<void>;
};

export type LogoutPlatform = "electron" | "web";

export interface SignOutStrategyOptions {
  locationLike?: BrowserLocation;
  redirectUri?: string;
  logoutReturnUri?: string;
  desktopRemoteTimeoutMs?: number;
}

export interface CompletePlatformSignOutOptions extends SignOutStrategyOptions {
  reload?: () => void;
  desktopReloadTimeoutMs?: number;
}

export interface SignOutStrategyResult {
  platform: LogoutPlatform;
  returnTo: string | null;
  remoteLogoutAttempted: boolean;
  remoteLogoutSucceeded: boolean;
}

function envRedirectUri(): string | undefined {
  return import.meta.env.VITE_WORKOS_REDIRECT_URI as string | undefined;
}

function envLogoutReturnUri(): string | undefined {
  return import.meta.env.VITE_WORKOS_LOGOUT_RETURN_URI as string | undefined;
}

function isCallbackPath(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.pathname === "/callback";
  } catch {
    return false;
  }
}

export function isElectronRuntime(): boolean {
  return typeof window !== "undefined" && (window as any).__electron === true;
}

/** Shell PTY — desktop app only; never on web (even if `__electron` is mocked). */
export function canUseShellTerminal(): boolean {
  if (typeof window === "undefined") return false;
  if (document.documentElement.getAttribute("data-runtime") === "web") {
    return false;
  }
  if (!isElectronRuntime()) return false;
  const api = (window as unknown as { __electronApi?: { ptySpawn?: unknown } })
    .__electronApi;
  return typeof api?.ptySpawn === "function";
}

export function isDesktopRuntime(_locationLike?: BrowserLocation): boolean {
  // Parameter is accepted for call-site compatibility — runtime detection no
  // longer depends on URL protocol now that the Tauri shell has been removed.
  void _locationLike;
  return isElectronRuntime();
}

/** True in a packaged desktop app (not dev on http://). Used for URL safeguards. */
export function isBuiltDesktopApp(): boolean {
  if (typeof window === "undefined") return false;
  if (isElectronRuntime() && window.location.protocol === "ano-app:")
    return true;
  return false;
}

/** True when running on a deployed *.ano.dev subdomain. */
export function isAnoDomain(
  locationLike: BrowserLocation = window.location,
): boolean {
  try {
    const hostname =
      "hostname" in locationLike
        ? (locationLike as { hostname: string }).hostname
        : new URL(locationLike.origin).hostname;
    return hostname.endsWith(".ano.dev") && locationLike.protocol === "https:";
  } catch {
    return false;
  }
}

export function toSafeHttpUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    // new URL("https://example.com").toString() → "https://example.com/"
    // WorkOS does exact-match on redirect URIs, so strip the trailing slash
    // added by URL normalization for bare origins (pathname === "/").
    const str = url.toString();
    return str.endsWith("/") && url.pathname === "/" ? str.slice(0, -1) : str;
  } catch {
    return null;
  }
}

export function resolveWorkosRedirectUri(
  locationLike: BrowserLocation = window.location,
  configuredRedirectUri: string | undefined = envRedirectUri(),
): string {
  // On *.ano.dev subdomains, use the current origin so OAuth
  // callbacks return to the workspace subdomain the user is on.
  // WorkOS wildcard redirect URI (https://*.ano.dev) handles all.
  if (isAnoDomain(locationLike)) {
    return toSafeHttpUrl(locationLike.origin) ?? locationLike.origin;
  }
  return toSafeHttpUrl(configuredRedirectUri) ?? locationLike.origin;
}

function resolveSignOutReturnTo(
  platform: LogoutPlatform,
  locationLike: BrowserLocation,
  options: SignOutStrategyOptions,
): string | null {
  const rawLogoutReturnUri = toSafeHttpUrl(
    options.logoutReturnUri ?? envLogoutReturnUri(),
  );
  const logoutReturnUri =
    rawLogoutReturnUri && !isCallbackPath(rawLogoutReturnUri)
      ? rawLogoutReturnUri
      : null;

  if (platform !== "web") {
    return logoutReturnUri;
  }

  // On *.ano.dev, return to current subdomain rather than hardcoded URI
  if (isAnoDomain(locationLike)) {
    return toSafeHttpUrl(locationLike.origin) ?? locationLike.origin;
  }

  const origin = toSafeHttpUrl(locationLike.origin);
  return logoutReturnUri ?? origin;
}

function clearWorkosClientStorageBestEffort() {
  try {
    for (const key of WORKOS_LOCAL_STORAGE_KEYS) {
      window.localStorage.removeItem(key);
    }
    window.sessionStorage.removeItem(WORKOS_ORGANIZATION_SESSION_KEY);
  } catch {
    // Best effort only.
  }
}

/**
 * Wipe the home-region cache populated by `resolveHomeRegion` /
 * `setHomeRegionForZero` (key: `ano:home-region:v1`).
 *
 * Region routing is static per sign-in: `<ZeroProvider>`'s `cacheURL`
 * is derived from this cache and never changes mid-session (a prior
 * reactive watcher was removed for violating Zero's URL/identity
 * invariant). The cache MUST be cleared on sign-out so a traveler
 * signing out + back in picks up the new region via timezone
 * detection — otherwise the cached value is permanently sticky and
 * the only way to switch regions is wiping localStorage by hand.
 *
 * Best-effort: private browsing / quota errors are swallowed. The
 * worst case is that the user is stuck on their previous region's
 * sync URL — slower but functionally correct (US authority serves
 * every workspace; EU users on `sync-us` just pay trans-Atlantic
 * latency).
 */
function clearHomeRegionCacheBestEffort() {
  try {
    window.localStorage.removeItem("ano:home-region:v1");
  } catch {
    // Best effort only — see jsdoc above.
  }
}

async function runWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<T | "timeout"> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<"timeout">((resolve) => {
    timeoutId = setTimeout(() => resolve("timeout"), timeoutMs);
  });
  const result = await Promise.race([promise, timeoutPromise]);
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  return result;
}

export async function signOutWithPlatformStrategy(
  signOut: AuthKitSignOut,
  options: SignOutStrategyOptions = {},
): Promise<SignOutStrategyResult> {
  const locationLike = options.locationLike ?? window.location;
  const platform: LogoutPlatform = isElectronRuntime() ? "electron" : "web";
  const returnTo = resolveSignOutReturnTo(platform, locationLike, options);

  // Clear the home-region cache eagerly — independent of the sign-out
  // outcome. Even if the remote logout below times out or fails, we
  // want the next sign-in's `resolveHomeRegion` to fall through to
  // timezone detection rather than restoring the previous region from
  // a still-warm cache.
  clearHomeRegionCacheBestEffort();

  if (platform !== "web") {
    if (!returnTo) {
      console.warn(
        "Skipping remote Auth logout in desktop app: configure VITE_WORKOS_LOGOUT_RETURN_URI with an http(s) URL.",
      );
      clearWorkosClientStorageBestEffort();
      return {
        platform,
        returnTo: null,
        remoteLogoutAttempted: false,
        remoteLogoutSucceeded: false,
      };
    }

    try {
      const result = await runWithTimeout(
        Promise.resolve(signOut({ navigate: false, returnTo })),
        options.desktopRemoteTimeoutMs ?? DEFAULT_DESKTOP_REMOTE_TIMEOUT_MS,
      );

      if (result === "timeout") {
        console.warn(
          `Timed out waiting for remote Auth logout in ${platform}.`,
        );
        clearWorkosClientStorageBestEffort();
        return {
          platform,
          returnTo,
          remoteLogoutAttempted: true,
          remoteLogoutSucceeded: false,
        };
      }

      clearWorkosClientStorageBestEffort();
      return {
        platform,
        returnTo,
        remoteLogoutAttempted: true,
        remoteLogoutSucceeded: true,
      };
    } catch (error) {
      console.warn(`Auth logout request failed in ${platform}`, error);
      clearWorkosClientStorageBestEffort();
      return {
        platform,
        returnTo,
        remoteLogoutAttempted: true,
        remoteLogoutSucceeded: false,
      };
    }
  }

  try {
    if (returnTo) {
      signOut({ returnTo });
    } else {
      signOut();
    }

    return {
      platform,
      returnTo,
      remoteLogoutAttempted: true,
      remoteLogoutSucceeded: true,
    };
  } catch (error) {
    console.warn("Auth logout request failed on web", error);
    return {
      platform,
      returnTo,
      remoteLogoutAttempted: true,
      remoteLogoutSucceeded: false,
    };
  }
}

export async function completePlatformSignOut(
  signOut: AuthKitSignOut,
  options: CompletePlatformSignOutOptions = {},
): Promise<SignOutStrategyResult> {
  const locationLike = options.locationLike ?? window.location;

  if (!isDesktopRuntime(locationLike)) {
    return signOutWithPlatformStrategy(signOut, options);
  }

  const reload = options.reload ?? (() => window.location.reload());
  let hasReloaded = false;

  const reloadOnce = () => {
    if (hasReloaded) return;
    hasReloaded = true;
    reload();
  };

  const timeoutId = setTimeout(
    reloadOnce,
    options.desktopReloadTimeoutMs ?? DEFAULT_DESKTOP_RELOAD_TIMEOUT_MS,
  );

  try {
    return await signOutWithPlatformStrategy(signOut, options);
  } finally {
    clearTimeout(timeoutId);
    reloadOnce();
  }
}

export interface CompleteSignOutAfterDeleteOptions {
  locationLike?: BrowserLocation;
  /** Override for tests. Defaults to navigating to the current origin's root. */
  navigate?: (target: string) => void;
}

/**
 * Sibling of `completePlatformSignOut` for the **account-deletion** flow.
 *
 * Account deletion deletes the WorkOS user as part of the server-side
 * cascade (see `server/routes/account-routes.ts:67-92`). By the time the
 * client wants to "sign out" afterwards, the WorkOS session_id no longer
 * resolves to a live user — calling WorkOS's `/sessions/logout` endpoint
 * with that session_id leaves the user stranded on a blank WorkOS-hosted
 * page (issue #1839, reported 2026-05-27).
 *
 * The fix is to skip the remote WorkOS logout entirely and just:
 *
 *   1. Clear the home-region cache (so re-signing-up picks fresh region
 *      detection — matches `signOutWithPlatformStrategy`'s eager clear).
 *   2. Clear all WorkOS client-side storage (refresh token, user blob,
 *      Zero JWT, etc.). The session is dead server-side; mirror that
 *      locally so the next render doesn't try to refresh.
 *   3. Navigate to the app root (or a caller-supplied target). For web
 *      this lands the user on the public sign-in surface. For Electron
 *      the renderer reloads against `ano-app://localhost`.
 *
 * No promise to await — local-only teardown, returns immediately so the
 * dialog's "Account deleted" toast can render before navigation kicks
 * the page over.
 */
export function completeSignOutAfterDelete(
  options: CompleteSignOutAfterDeleteOptions = {},
): void {
  clearHomeRegionCacheBestEffort();
  clearWorkosClientStorageBestEffort();

  const locationLike = options.locationLike ?? window.location;
  const navigate =
    options.navigate ??
    ((target: string) => {
      window.location.href = target;
    });
  navigate(locationLike.origin);
}
